import {
  Box,
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Switch,
  Table,
  TableContainer,
  Tag,
  Tbody,
  Tooltip,
  Td,
  Text,
  Textarea,
  Th,
  Thead,
  Tr,
  useDisclosure,
  useToast,
  VStack
} from '@chakra-ui/react';
import React, {
  useContext,
  useEffect,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import FilterTextBox from '../../components/FilterTextBox';
import { WebAPIContext } from '../../contexts/WebAPIContext';
import { Messages, InputLimits } from '../../contexts/constants';
import { supportedLngs } from '../../utils/i18n';
import {
  NotificationType,
  Errors,
  BilingualName,
  LanguageCode,
} from '../../contexts/types';
import { CustomNumberInput } from '../InvestmentScreening/CustomNumberInput';
import { formatLocalDateTime, formatDateByLocale } from '../../utils/utils';

/**
 * マスタメンテナンス - お知らせ管理 画面
 */
const Notification: React.FC = () => {
  const { t } = useTranslation();
  // Toastメッセージを表示するためのフック
  const toast = useToast();
  // WebAPIContextを使用してAPI操作を行う
  const WebAPI = useContext(WebAPIContext);
  /** SUPPORTED_LANGUAGES/supportedLngsに追従する動的言語リスト */
  const langs = Object.keys(supportedLngs) as LanguageCode[];

  /** タイトル */
  const TITLE = t('features.MasterMaintenance.Notification.title');
  /** エンティティ略称 */
  const ENTITY = t('features.MasterMaintenance.Notification.entityLabel');

  /** ヘッダー部セルスタイル */
  const headerCellStyle = {
    fontWeight: "bold",
    fontSize: "14px",
    whiteSpace: "nowrap",
    backgroundColor: "gray.100",
  };

  /** ボディ部セルスタイル */
  const bodyCellStyle = {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    bgColor: "white",
  }

  /** 列固定スタイル(1列目) */
  const fixedCol1Style = {
    position: "sticky" as const,
    left: "0",
    zIndex: "1",
    boxShadow: "2px 0 4px rgba(0,0,0,0.1)",
  }

  /** ホバースタイル */
  const hoverStyle = {
    bg: "gray.200"
  }

  /** 項目（ラベル）スタイル */
  const labelStyle = {
    fontWeight: "bold",
    whiteSpace: "normal" as const,
    wordBreak: "break-word" as const,
    marginTop: "8px",
    width: 170,
  }

  /** 項目（値）幅 */
  const valueWidth = 475;

  /** お知らせマスタデータ */
  const [masterData, setMasterData] = useState<NotificationType[]>([]);

  /** 編集対象お知らせマスタの初期値 */
  const initialEditTarget: NotificationType = {
    notificationId: "",
    title: langs.reduce((acc, lang) => { acc[lang] = ""; return acc; }, {} as BilingualName),
    body: langs.reduce((acc, lang) => { acc[lang] = ""; return acc; }, {} as BilingualName),
    publishDate: "",
    isPublished: false,
    order: 0,
    deleted: false,
    createDate: "",
    updateDate: "",
  };

  /** 編集対象お知らせマスタ */
  const [editTarget, setEditTarget] = useState<NotificationType>(initialEditTarget);

  /** エラー情報 */
  const [errors, setErrors] = useState<Errors>({});

  /** モーダル表示制御用 */
  const { isOpen: isOpenEditModal, onOpen: onOpenEditModal, onClose: onCloseEditModal } = useDisclosure();

  // （フィルター）タイトル
  const [filterTitle, setFilterTitle] = useState<string>("");

  // 編集モーダル保存ボタンの活性制御
  const [isSaving, setIsSaving] = useState(false);

  /** フィルター済みマスタデータ */
  const filteredMasterData = (masterData ?? []).filter(m => {
    if (!m) {
      return false;
    }

    // 指定した値が含まれているかを返す
    const hasFilterString = (propertyValue: string | null | undefined, filterString: string): boolean => {
      return (propertyValue ?? '').toLowerCase().includes(filterString.toLowerCase());
    };

    return langs.some((lang) => hasFilterString(m.title?.[lang], filterTitle));
  });

  /**
   * 画面初期表示
   */
  useEffect(() => {
    const fetch = async () => {
      await fetchMasterData();
    }

    // 実行
    fetch();
  }, []);

  /**
   * 「新規登録」ボタン押下
   */
  const handleOpenEditModal = async () => {
    onOpenEditModal();
  }

  /**
   * 指定したプロパティ名の値を変更
   * @param propertyName プロパティ名
   * @param value 値
   */
  const changeEditTarget = (propertyName: string, value: any) => {
    setEditTarget((prevState) => ({
      ...prevState,
      [propertyName]: value,
    }));
  }

  /**
   * 対象のマスタデータ取得
   */
  const fetchMasterData = async () => {
    try {
      const payload = {
        query: `
          query {
            getNotifications(hasDeleted: true) {
              notificationId
              title { ${langs.join(' ')} }
              body { ${langs.join(' ')} }
              publishDate
              isPublished
              deleted
              order
              createDate
              updateDate
            }
          }`
      };

      // データ取得実行
      const response = await WebAPI.request(payload);

      if (response?.data?.getNotifications) {
        // 削除フラグがfalseのものを先に、公開日の降順でソート
        const sorted = [...response.data.getNotifications].sort((a: NotificationType, b: NotificationType) => {
          // 削除フラグ: false を先に
          if (a.deleted !== b.deleted) {
            return a.deleted ? 1 : -1;
          }
          // 公開日の降順（新しいもの順）
          const dateA = a.publishDate || '';
          const dateB = b.publishDate || '';
          if (dateA !== dateB) {
            return dateB.localeCompare(dateA);
          }
          // 表示順（昇順）
          const orderA = a.order ?? 0;
          const orderB = b.order ?? 0;
          return orderA - orderB;
        });
        setMasterData(sorted);
      } else {
        console.error(`Failed to call API getNotifications. errors = `, response?.errors);
        toast({
          title: TITLE,
          description: t('common.message.fetchListFailed', { name: ENTITY }),
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }

    } catch (error) {
      console.error("Failed. fetchData", error);
      toast({
        title: TITLE,
        description: `お知らせ一覧取得に失敗しました。`,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  }

  /**
   * 「タイトル」リンク押下
   * @param notificationId お知らせID
   */
  const handleEdit = async (notificationId?: string | null) => {
    try {
      const payload = {
        query: `
          query getNotificationById($notificationId: String) {
            getNotificationById(notificationId: $notificationId) {
              notificationId
              title { ja en }
              body { ja en }
              publishDate
              isPublished
              deleted
              order
              createDate
              updateDate
            }
          }`,
        variables: { notificationId }
      };

      // データ取得実行
      const response = await WebAPI.request(payload);

      if (response?.data?.getNotificationById) {
        setEditTarget(response?.data?.getNotificationById);
        // 編集モーダル表示
        onOpenEditModal();
      } else {
        console.error(`Failed to call API getNotificationById. errors = `, response?.errors);
        toast({
          title: TITLE,
          description: t('common.message.fetchInfoFailed', { name: ENTITY }),
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }

    } catch (error) {
      console.error("Failed. fetchData", error);
      toast({
        title: TITLE,
        description: `お知らせ情報取得に失敗しました。`,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  }

  /**
   * 「保存」ボタン押下
   */
  const handleSave = async () => {
    if (isSaving) return;
    setIsSaving(true);

    if (!validate()) {
      toast({
        title: t('common.label.inputError'),
        description: t('common.message.inputErrorContent'),
        status: 'warning',
        duration: 2000,
        isClosable: true,
      });
      setIsSaving(false);
      return;
    }

    // 登録・更新
    if (!await putEditTarget()) {
      setIsSaving(false);
      return;
    }

    // 一覧更新
    await fetchMasterData();

    // モーダル閉じる
    setIsSaving(false);
    onCloseEditModal();
  }

  /**
   * バリデーションチェックを行う。
   * @returns true：検証成功、false：検証失敗
   */
  const validate = () => {
    setErrors({});

    const localErrors: Errors = {};

    // 必須項目チェック
    const required = (value?: string | null) => Boolean(value || null);

    // 必須項目チェックとエラー情報設定
    const requiredAndSetError = (target: any, propertyName: string, propertyNameJp: string, errorKey: string = propertyName) => {
      const requiredResult = required(target[propertyName]);

      if (!requiredResult) {
        localErrors[errorKey] = {
          isError: true,
          errorMessage: t('common.message.requiredField', { fieldName: propertyNameJp }),
        };
      }
      return requiredResult;
    }

    let result = true;

    langs.forEach((lang) => {
      const CapitalizedLang = lang.charAt(0).toUpperCase() + lang.slice(1);
      const errorKey = `title${CapitalizedLang}`;
      const translationKey = lang === 'ja'
        ? 'features.MasterMaintenance.Notification.field.title'
        : `features.MasterMaintenance.Notification.field.title${CapitalizedLang}`;

      if (!requiredAndSetError(editTarget.title, lang, t(translationKey), errorKey)) {
        result = false;
      }
    });

    // 公開フラグがtrueの場合、公開日は必須
    if (editTarget.isPublished) {
      if (!requiredAndSetError(editTarget, "publishDate", t('features.MasterMaintenance.Notification.field.publishDate'))) {
        result = false;
      }
    }

    setErrors(localErrors);
    return result;
  }

  /**
   * 入力したデータを登録・更新
   */
  const putEditTarget = async () => {
    try {
      // 登録パラメータ
      const input = {
        notificationId: editTarget.notificationId || undefined,
        title: langs.reduce((acc, lang) => {
          acc[lang] = editTarget.title?.[lang] || null;
          return acc;
        }, {} as Record<string, string | null>),
        body: langs.reduce((acc, lang) => {
          acc[lang] = editTarget.body?.[lang] || null;
          return acc;
        }, {} as Record<string, string | null>),
        publishDate: editTarget.publishDate,
        isPublished: editTarget.isPublished,
        order: editTarget.order ?? 0,
        deleted: editTarget.deleted,
        updateDate: editTarget.updateDate || undefined,
      };

      // AppSync
      const result = await WebAPI.request({
        query: `
          mutation putNotification($input: NotificationInput!) {
            putNotification(input: $input) {status}
          }
        `,
        variables: {
          input: input,
        }
      });

      if (result.data.putNotification.status === "success") {
        toast({
          title: TITLE,
          description: t('common.message.inputSaved'),
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
        console.log(`Success to call API putNotification.`);
        return true;
      } else if (result?.data?.putNotification.status === 'expired') {
        toast({
          title: TITLE,
          description: Messages.expiredMessage,
          status: 'warning',
          duration: 5000,
          isClosable: true,
        });
        console.warn(`Conflict error. putNotification.`);
        return false;
      } else {
        toast({
          title: TITLE,
          description: t('common.message.saveFailed'),
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
        console.error(`Failed to call API putNotification. errors = ${JSON.stringify(result?.errors)}`);
        return false;
      }
    } catch (error) {
      console.error("Exception to call API putNotification. error = ", error);
      toast({
        title: TITLE,
        description: t('common.message.saveFailed'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return false;
    }
  }

  /**
   * 「閉じる」ボタン押下
   */
  const handleClose = async () => {
    // 一覧更新
    await fetchMasterData();
    onCloseEditModal();
  }

  /**
   * 編集モーダル閉じる時、初期化する
   */
  const handleCloseComplete = () => {
    // 初期化
    setEditTarget(initialEditTarget);
    setErrors({});
  }

  /**
   * レイアウト
   */
  return (
    <>
      <Box h="100%" mt="16px">
        <Box>
          <Button colorScheme='blue' onClick={(e) => {
            e.currentTarget.blur();
            handleOpenEditModal()
          }}>{t('common.button.newRegistration')}</Button>
        </Box>
        <TableContainer overflowX="auto" overflowY="auto" h="calc(100dvh - 150px)" mt={2}>
          <Table variant="simple" style={{ tableLayout: "fixed", borderCollapse: "separate" }} cellSpacing={0} cellPadding={0} className="mm-ceas-table">
            <colgroup>
              {langs.map((lang) => (
                <col key={`col-title-${lang}`} width={lang === 'ja' ? 300 : 200} />
              ))}
              <col width={150} />
              <col width={100} />
              <col width={80} />
              <col width={100} />
              <col width={200} />
              <col width={200} />
            </colgroup>
            <Thead position="sticky" top={0} zIndex={2} boxShadow={`0 -1px 0 #EEEEEE`}>
              <Tr h={50}>
                {langs.map((lang) => {
                  const CapitalizedLang = lang.charAt(0).toUpperCase() + lang.slice(1);
                  const translationKey = lang === 'ja'
                    ? 'features.MasterMaintenance.Notification.field.title'
                    : `features.MasterMaintenance.Notification.field.title${CapitalizedLang}`;
                  return (
                    <Th {...headerCellStyle} {...(lang === 'ja' ? fixedCol1Style : {})} key={`th-title-${lang}`}>
                      {t(translationKey)}
                      {lang === 'ja' && (
                        <FilterTextBox placeholder={t('common.placeholder.partialMatch')} filterValue={filterTitle} setFilterValue={setFilterTitle} />
                      )}
                    </Th>
                  );
                })}
                <Th {...headerCellStyle}>{t('features.MasterMaintenance.Notification.field.publishDate')}</Th>
                <Th {...headerCellStyle}>{t('features.MasterMaintenance.Notification.field.publishState')}</Th>
                <Th {...headerCellStyle}>{t('common.label.sortOrder')}</Th>
                <Th {...headerCellStyle}>{t('common.label.deleteFlag')}</Th>
                <Th {...headerCellStyle}>{t('common.label.registrationDate')}</Th>
                <Th {...headerCellStyle}>{t('common.label.updateDate')}</Th>
              </Tr>
            </Thead>
            <Tbody>
              {filteredMasterData.map(record => {
                const bgColor = record.deleted ? "gray.100" : "white";

                return (
                  <Tr role="group" key={record.notificationId} _hover={{ bg: "gray.200" }}>
                    {langs.map((lang) => (
                      <Td {...bodyCellStyle} _groupHover={hoverStyle} bgColor={bgColor} {...(lang === 'ja' ? fixedCol1Style : {})} key={`td-title-${lang}-${record.notificationId}`}>
                        {lang === 'ja' ? (
                          <Tooltip label={record.title?.ja} placement="top" hasArrow>
                            <Button
                              variant="link"
                              textDecoration="underline"
                              color="blue.500"
                              onClick={(e) => {
                                e.currentTarget.blur();
                                handleEdit(record.notificationId)
                              }}
                              sx={{
                                display: 'inline-block',
                                maxW: '100%',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                              }}
                            >
                              {record.title?.[lang]}
                            </Button>
                          </Tooltip>
                        ) : (
                          <Tooltip label={record.title?.[lang]} placement="top" hasArrow>
                            <Text isTruncated >{record.title?.[lang]}</Text>
                          </Tooltip>
                        )}
                      </Td>
                    ))}
                    <Td {...bodyCellStyle} _groupHover={hoverStyle} bgColor={bgColor}>{formatDateByLocale(record.publishDate)}</Td>
                    <Td {...bodyCellStyle} _groupHover={hoverStyle} bgColor={bgColor} textAlign="center" overflow="visible">
                      {record.isPublished ? (
                        <Tag size="md" colorScheme='blue' whiteSpace="nowrap">{t('features.MasterMaintenance.Notification.label.published')}</Tag>
                      ) : (
                        <Tag size="md" colorScheme='gray' whiteSpace="nowrap">{t('features.MasterMaintenance.Notification.label.draft')}</Tag>
                      )}
                    </Td>
                    <Td {...bodyCellStyle} _groupHover={hoverStyle} bgColor={bgColor} textAlign="right">{record.order}</Td>
                    <Td {...bodyCellStyle} _groupHover={hoverStyle} bgColor={bgColor} textAlign="center">{record.deleted ? (
                      <Tag size="lg" colorScheme='red'>{String(record.deleted)}</Tag>
                    ) : (
                      <Tag size="lg" colorScheme='green'>{String(record.deleted)}</Tag>
                    )
                    }</Td>
                    <Td {...bodyCellStyle} _groupHover={hoverStyle} bgColor={bgColor}>{formatLocalDateTime(record.createDate)}</Td>
                    <Td {...bodyCellStyle} _groupHover={hoverStyle} bgColor={bgColor}>{formatLocalDateTime(record.updateDate)}</Td>
                  </Tr>
                )
              })}
            </Tbody>
          </Table>
        </TableContainer>
      </Box >

      {/* 編集モーダル */}
      <Modal isOpen={isOpenEditModal} onClose={onCloseEditModal} closeOnOverlayClick={false} onCloseComplete={handleCloseComplete} scrollBehavior="inside" size="3xl" isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            <Text>{t('features.MasterMaintenance.Notification.modal.editTitle')}</Text>
          </ModalHeader>
          <ModalBody>
            <VStack>

              {/* お知らせID（編集時のみ表示） */}
              {editTarget.updateDate && (
                <FormControl h={70}>
                  <Flex align="start">
                    <FormLabel style={labelStyle}>
                      {t('features.MasterMaintenance.Notification.field.id')}
                    </FormLabel>
                    <VStack align="left">
                      <Input
                        value={editTarget.notificationId || ""}
                        width={valueWidth}
                        isReadOnly
                        bg="gray.100"
                      />
                    </VStack>
                  </Flex>
                </FormControl>
              )}

              {/* タイトル（多言語）入力欄: SUPPORTED_LANGUAGES/supportedLngsに追従する動的ループ */}
              {langs.map((lang) => {
                const CapitalizedLang = lang.charAt(0).toUpperCase() + lang.slice(1);
                const errorKey = `title${CapitalizedLang}`;
                const fieldTranslationKey = lang === 'ja'
                  ? 'features.MasterMaintenance.Notification.field.title'
                  : `features.MasterMaintenance.Notification.field.title${CapitalizedLang}`;
                const placeholderTranslationKey = lang === 'ja'
                  ? 'features.MasterMaintenance.Notification.placeholder.title'
                  : `features.MasterMaintenance.Notification.placeholder.title${CapitalizedLang}`;

                return (
                  <FormControl isInvalid={errors[errorKey]?.isError} h={70} key={`fc-title-${lang}`}>
                    <Flex align="start">
                      <FormLabel style={labelStyle}>
                        {t(fieldTranslationKey)}
                      </FormLabel>
                      <VStack align="left">
                        <Input
                          value={editTarget.title?.[lang] || ""}
                          onChange={(event) => { changeEditTarget("title", { ...editTarget.title, [lang]: event.target.value }) }}
                          placeholder={t(placeholderTranslationKey)}
                          width={valueWidth}
                          maxLength={InputLimits.NOTIFICATION_TITLE}
                          isInvalid={errors[errorKey]?.isError}
                          autoComplete="off"
                        />
                        <FormErrorMessage mt={0}>{errors[errorKey]?.errorMessage}</FormErrorMessage>
                      </VStack>
                    </Flex>
                  </FormControl>
                );
              })}

              {/* 本文（多言語）入力欄: SUPPORTED_LANGUAGES/supportedLngsに追従する動的ループ */}
              {langs.map((lang) => {
                const CapitalizedLang = lang.charAt(0).toUpperCase() + lang.slice(1);
                const fieldTranslationKey = lang === 'ja'
                  ? 'features.MasterMaintenance.Notification.field.body'
                  : `features.MasterMaintenance.Notification.field.body${CapitalizedLang}`;
                const placeholderTranslationKey = lang === 'ja'
                  ? 'features.MasterMaintenance.Notification.placeholder.body'
                  : `features.MasterMaintenance.Notification.placeholder.body${CapitalizedLang}`;

                return (
                  <FormControl key={`fc-body-${lang}`}>
                    <Flex align="start">
                      <FormLabel style={labelStyle}>
                        {t(fieldTranslationKey)}
                      </FormLabel>
                      <VStack align="left">
                        <Textarea
                          value={editTarget.body?.[lang] || ""}
                          onChange={(event) => { changeEditTarget("body", { ...editTarget.body, [lang]: event.target.value }) }}
                          placeholder={t(placeholderTranslationKey)}
                          width={valueWidth}
                          rows={6}
                          maxLength={InputLimits.NOTIFICATION_BODY}
                        />
                      </VStack>
                    </Flex>
                  </FormControl>
                );
              })}

              <FormControl isInvalid={errors.publishDate?.isError} h={70} mt={2}>
                <Flex align="start">
                  <FormLabel style={labelStyle}>
                    {t('features.MasterMaintenance.Notification.field.publishDate')}
                  </FormLabel>
                  <VStack align="left">
                    <Input
                      type="date"
                      value={editTarget.publishDate || ""}
                      onChange={(event) => { changeEditTarget("publishDate", event.target.value) }}
                      width={valueWidth}
                      isInvalid={errors.publishDate?.isError}
                    />
                    <FormErrorMessage mt={0}>{errors.publishDate?.errorMessage}</FormErrorMessage>
                  </VStack>
                </Flex>
              </FormControl>

              <FormControl h={70}>
                <Flex align="start">
                  <FormLabel style={labelStyle}>
                    {t('features.MasterMaintenance.Notification.field.publishFlag')}
                  </FormLabel>
                  <VStack align="left">
                    <HStack>
                      <Button fontWeight="bold" userSelect="none" cursor="pointer" variant="ghost" _hover={{ bg: 'transparent' }}
                        onClick={() => {
                          changeEditTarget("isPublished", false);
                        }}>{t('features.MasterMaintenance.Notification.label.draft')}</Button>

                      <Switch
                        isChecked={editTarget.isPublished}
                        onChange={(event) => { changeEditTarget("isPublished", event.target.checked) }}
                      />

                      <Button fontWeight="bold" userSelect="none" cursor="pointer" variant="ghost" _hover={{ bg: 'transparent' }}
                        onClick={() => {
                          changeEditTarget("isPublished", true)
                        }}>{t('features.MasterMaintenance.Notification.label.published')}</Button>
                    </HStack>
                  </VStack>
                </Flex>
              </FormControl>

              <FormControl h={70}>
                <Flex align="start">
                  <FormLabel style={labelStyle}>
                    {t('common.label.sortOrder')}
                  </FormLabel>
                  <VStack align="left">
                    <CustomNumberInput
                      value={editTarget.order || 0}
                      onChange={(value) => { changeEditTarget("order", value) }}
                      placeholder={t('common.label.sortOrder')}
                      min={0}
                      max={9999}
                      decimalPlaces={0}
                      w={150}
                    />
                  </VStack>
                </Flex>
              </FormControl>

              <FormControl h={70}>
                <Flex align="start">
                  <FormLabel style={labelStyle}>
                    {t('common.label.deleteFlag')}
                  </FormLabel>
                  <VStack align="left">
                    <HStack>
                      <Button fontWeight="bold" userSelect="none" cursor="pointer" variant="ghost" _hover={{ bg: 'transparent' }}
                        onClick={() => {
                          changeEditTarget("deleted", false);
                        }}>{t('common.label.notDeleted')}</Button>

                      <Switch
                        isChecked={editTarget.deleted}
                        onChange={(event) => { changeEditTarget("deleted", event.target.checked) }}
                      />

                      <Button fontWeight="bold" userSelect="none" cursor="pointer" variant="ghost" _hover={{ bg: 'transparent' }}
                        onClick={() => {
                          changeEditTarget("deleted", true)
                        }}>{t('common.label.deleted')}</Button>
                    </HStack>
                  </VStack>
                </Flex>
              </FormControl>

              <FormControl>
                <Flex align="start">
                  <FormLabel style={labelStyle}>
                    {t('common.label.createDate')}
                  </FormLabel>
                  <VStack align="left">
                    <Text>{formatLocalDateTime(editTarget.createDate)}</Text>
                  </VStack>
                </Flex>
              </FormControl>

              <FormControl>
                <Flex align="start">
                  <FormLabel style={labelStyle}>
                    {t('common.label.updateDate')}
                  </FormLabel>
                  <VStack align="left">
                    <Text>{formatLocalDateTime(editTarget.updateDate)}</Text>
                  </VStack>
                </Flex>
              </FormControl>

            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="blue" isDisabled={isSaving} isLoading={isSaving} onClick={(e) => {
              e.currentTarget.blur();
              handleSave();
            }} m={1}>
              {t('common.button.save')}
            </Button>
            <Button onClick={handleClose} m={1}>
              {t('common.button.close')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
}

export default Notification;
